import { useEffect, useImperativeHandle, useRef, useState } from "react";
import { Eraser } from "lucide-react";

/** Zone de signature au doigt / à la souris / au stylet. ref : { isEmpty(), toDataURL(), clear() } */
export function SignaturePad({ ref, height = 260, onChange }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [empty, setEmpty] = useState(true);

  const setup = () => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    const w = c.clientWidth;
    c.width = w * ratio;
    c.height = height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, w, height);
    ctx.lineWidth = 2.4;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";
  };

  useEffect(() => {
    setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture?.(e.pointerId);
    drawing.current = true;
    last.current = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.arc(last.current.x, last.current.y, 1.1, 0, Math.PI * 2);
    ctx.fillStyle = "#111827";
    ctx.fill();
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (empty) { setEmpty(false); onChange?.(false); }
  };
  const up = () => { drawing.current = false; };
  const clear = () => { setup(); setEmpty(true); onChange?.(true); };

  useImperativeHandle(ref, () => ({
    isEmpty: () => empty,
    toDataURL: () => canvasRef.current.toDataURL("image/png"),
    clear,
  }));

  return (
    <div className="space-y-2">
      <div className="relative rounded-lg border-2 border-dashed border-border overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height, touchAction: "none", display: "block", cursor: "crosshair" }}
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerLeave={up}
          data-testid="signature-pad"
        />
        {empty && <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-400">Signez ici</span>}
        <span className="pointer-events-none absolute left-6 right-6 bottom-10 border-b border-gray-300" />
      </div>
      <button type="button" onClick={clear} className="text-xs inline-flex items-center gap-1 text-muted-foreground hover:text-foreground">
        <Eraser size={13} /> Effacer
      </button>
    </div>
  );
}
